import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText } from "lucide-react";
import { useStore } from "../lib/store";
import { rangeLabel, shiftDay, type PeriodId } from "../features/leads/list";
import { funnelFlow, homeRange, previousRange, smoothPath, type DayRange } from "../features/home/metrics";
import {
  analysisGaps,
  approachConversion,
  channelPace,
  channelPerformance,
  clientTenureMonths,
  monthlyRevenue,
  topDecileCents,
  utilizationSeries,
  type MonthRevenue,
} from "../features/analysis/metrics";
import {
  clientValueSummary,
  clientValues,
  overdueInvoices,
  projectedCents,
  projectionSeries,
  revenueByChannel,
} from "../features/finance/analytics";
import { Button } from "../ui/Button";
import { CompactSelect } from "../ui/CompactSelect";
import { DonutSlices } from "../ui/charts";
import { EmptyState } from "../ui/EmptyState";
import { HelpPopover } from "../ui/HelpPopover";
import { invoiceBalanceCents } from "../features/finance/contracts";
import { money } from "./lista-popovers";
import { appPath } from "../app/routes";
import { ReportDialog } from "./ReportDialog";
import "./analise.css";

/**
 * Análise — porte da `#page-analise` do Zênite: fichas de período, funil de
 * conversão com a passagem entre etapas, diagnóstico, desempenho por canal,
 * receita contra a meta, projeção, LTV, receita por canal, conversão por
 * abordagem, aproveitamento e ritmo dos canais.
 *
 * Toda fórmula vive em `features/analysis/metrics.ts` e em
 * `features/finance/analytics.ts` (§5); cada bloco declara período, unidade e
 * denominador no "?" ao lado do título (§10). O "Diagnóstico da IA" mantém o
 * nome do protótipo por decisão do proprietário (§24), mas o que ele faz é
 * contar regras fixas: o "?" ao lado do título diz isso em voz alta, para o
 * nome não prometer capacidade que não existe (§3).
 */

/** As três fichas do protótipo. `ano` fica indisponível enquanto faltar histórico. */
const CHIPS: Array<{ id: PeriodId | "ano"; label: string }> = [
  { id: "mes", label: "30 dias" },
  { id: "tri", label: "Trimestre" },
  { id: "ano", label: "12 meses" },
];

/* As cores dos canais do protótipo (`PERF`): azul do gradiente, âmbar, rosa,
   verde e violeta — vivas nos dois temas porque vêm dos tokens. */
const CANAL_CORES = [
  "var(--grad-a)",
  "var(--warn)",
  "var(--proposal)",
  "var(--good)",
  "var(--accent-2)",
];

const PROJECAO = [
  { value: "1", label: "Amanhã" },
  { value: "7", label: "Próximos 7 dias" },
  { value: "30", label: "Próximos 30 dias" },
  { value: "90", label: "Próximos 90 dias" },
];

const moneyCents = (cents: number) => money(Math.round(cents / 100));

/** Forma curta do protótipo para o centro do donut: `R$ 18,1k`. */
/**
 * Forma curta do protótipo para régua, rótulo de barra e centro do donut:
 * `8k`, `32k`, `1,2M`. O valor exato continua na tabela, no "?" e no texto
 * alternativo do gráfico — o curto serve para caber, não para substituir.
 */
function short(reais: number): string {
  const value = Math.round(reais);
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}k`;
  return String(value);
}

function isPeriod(value: string | null): value is PeriodId {
  return value === "mes" || value === "tri";
}

function percent(ratio: number | null, digits = 0): string {
  if (ratio === null) return "—";
  return `${(ratio * 100).toFixed(digits).replace(".", ",")}%`;
}

/** `AAAA-MM-DD` → `DD/MM`, para os eixos. */
function shortDay(day: string): string {
  return `${day.slice(8)}/${day.slice(5, 7)}`;
}

export function AnalisePage() {
  const store = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [horizon, setHorizon] = useState("30");
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Entrar na rota põe o foco no início da superfície (AGENTS §17).
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, []);

  const now = useMemo(() => new Date().toISOString(), []);
  const today = now.slice(0, 10);
  const periodParam = params.get("periodo");
  const period: PeriodId = isPeriod(periodParam) ? periodParam : "tri";
  const range = useMemo(() => homeRange(period, now), [period, now]);
  const previous = useMemo(() => previousRange(range), [range]);
  const periodo = rangeLabel(range);
  /* Nos cartões estreitos a referência usa a forma curta do período; o intervalo
     completo continua no "?" e na linha abaixo do título. */
  const periodoCurto = period === "mes" ? "últimos 30 dias" : "no trimestre";

  const funnels = useMemo(() => store.funnels.filter((item) => !item.archived), [store.funnels]);
  const funnelParam = params.get("funil");
  const funnelId = funnels.some((item) => item.id === funnelParam) ? funnelParam! : (funnels[0]?.id ?? "");
  const funnel = funnels.find((item) => item.id === funnelId);
  const receipts = useMemo(() => store.receipts ?? [], [store.receipts]);
  const invoices = useMemo(() => store.invoices ?? [], [store.invoices]);
  const goals = useMemo(() => store.revenueGoals ?? [], [store.revenueGoals]);
  const activeLeads = useMemo(() => store.leads.filter((lead) => !lead.archived), [store.leads]);

  const flow = useMemo(
    () =>
      funnelId
        ? funnelFlow({
            funnelId,
            stages: store.stages,
            memberships: store.memberships,
            leads: activeLeads,
            events: store.events,
            range,
          })
        : [],
    [funnelId, store.stages, store.memberships, store.events, activeLeads, range],
  );
  const gaps = useMemo(
    () =>
      analysisGaps({
        funnelId,
        stages: store.stages,
        memberships: store.memberships,
        leads: activeLeads,
        events: store.events,
        invoices,
        receipts,
        range,
        now,
      }),
    [funnelId, store.stages, store.memberships, store.events, activeLeads, invoices, receipts, range, now],
  );
  const channels = useMemo(
    () => channelPerformance({ leads: activeLeads, memberships: store.memberships, receipts, range, previous }),
    [activeLeads, store.memberships, receipts, range, previous],
  );
  const months = useMemo(() => monthlyRevenue(receipts, goals, 6, now), [receipts, goals, now]);
  const approaches = useMemo(
    () => approachConversion(activeLeads, store.memberships, store.triggers, range),
    [activeLeads, store.memberships, store.triggers, range],
  );
  const utilization = useMemo(() => utilizationSeries(store.memberships, range), [store.memberships, range]);
  const pace = useMemo(() => channelPace(receipts, activeLeads, range), [receipts, activeLeads, range]);

  const values = useMemo(() => clientValues(activeLeads, receipts), [activeLeads, receipts]);
  const ltv = useMemo(() => clientValueSummary(values), [values]);
  const tenure = useMemo(() => clientTenureMonths(activeLeads, now), [activeLeads, now]);
  const byChannel = useMemo(() => revenueByChannel(receipts, range), [receipts, range]);
  const donutTotal = byChannel.reduce((sum, row) => sum + row.cents, 0);
  const projection = useMemo(
    () => projectedCents(invoices, receipts, today, Number(horizon)),
    [invoices, receipts, today, horizon],
  );
  const projectedDaily = useMemo(
    () => projectionSeries(invoices, receipts, today, Number(horizon)),
    [invoices, receipts, today, horizon],
  );
  /* A janela começa amanhã: fatura que vence hoje já é de hoje, não projeção. */
  const janela = useMemo(() => {
    const dias = Number(horizon);
    return `${shortDay(shiftDay(today, 1))} – ${shortDay(shiftDay(today, dias))}`;
  }, [today, horizon]);
  const overdue = useMemo(
    () =>
      overdueInvoices(invoices, receipts, today).reduce(
        (sum, invoice) => sum + invoiceBalanceCents(invoice, receipts),
        0,
      ),
    [invoices, receipts, today],
  );

  const [reportOpen, setReportOpen] = useState(false);

  return (
    <div className="zn-analise">
      <div className="pagehead">
        <div>
          <h1 ref={titleRef} tabIndex={-1}>
            Análise
          </h1>
          <p className="sub">O que os números dizem — e onde a IA vê espaço para melhorar</p>
        </div>
        <div className="actions">
          <div className="chips" role="group" aria-label="Período da análise">
            {CHIPS.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className={chip.id === period ? "active" : undefined}
                aria-pressed={chip.id === period}
                disabled={chip.id === "ano"}
                title={
                  chip.id === "ano"
                    ? "A visão de 12 meses precisa de um histórico maior do que o registrado até aqui."
                    : undefined
                }
                onClick={() => {
                  if (chip.id === "ano") return;
                  const next = new URLSearchParams(params);
                  if (chip.id === "tri") next.delete("periodo");
                  else next.set("periodo", chip.id);
                  setParams(next, { replace: true });
                }}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <Button variant="primary" icon={<FileText aria-hidden="true" />} onClick={() => setReportOpen(true)}>
            Gerar relatório
          </Button>
        </div>
      </div>

      <p className="z-help">
        {periodo} · {funnel?.name ?? "sem funil selecionado"}. A visão de 12 meses precisa de um histórico maior do
        que o registrado até aqui.
      </p>

      <div className="ana-grid">
        <section className="card">
          <div className="card-title">
            <h2>Funil de conversão</h2>
            <HelpPopover
              about="o funil"
              label="Sobre o funil de conversão"
              items={[
                {
                  term: "Chegaram à etapa",
                  description:
                    "Uma participação conta na etapa quando está nela, quando já passou por ela (evento de etapa) ou quando foi ganha. O denominador é o total de participações do funil criadas no período.",
                },
                {
                  term: "Passam",
                  description: "Participações que chegaram à etapa seguinte dividido pelas que chegaram a esta.",
                },
              ]}
            />
            <span className="hint">
              {periodo} · {flow[0]?.reached ?? 0} participações
            </span>
            {funnels.length > 0 && (
              <CompactSelect
                className="ana-funnel"
                prefix="Funil"
                options={funnels.map((item) => ({ value: item.id, label: item.name }))}
                value={funnelId}
                align="end"
                onChange={(value) => {
                  const next = new URLSearchParams(params);
                  if (value === funnels[0]?.id) next.delete("funil");
                  else next.set("funil", value);
                  setParams(next, { replace: true });
                }}
              />
            )}
          </div>
          {flow.length === 0 ? (
            <EmptyState
              title="Sem etapas neste funil"
              description="Configure as etapas do funil para acompanhar a conversão."
            />
          ) : (
            <div className="fun-rows">
              {flow.map((row, index) => {
                const next = flow[index + 1];
                const passam = next && row.reached > 0 ? next.reached / row.reached : null;
                return (
                  <div key={row.stage.id}>
                    <div className="fun-row">
                      <span className="fun-name">{row.stage.name}</span>
                      <div className="fun-bar-wrap">
                        <div className="fun-bar" style={{ width: `${Math.max(row.ratio, 0.07) * 100}%` }}>
                          <span className="num">{row.reached}</span>
                        </div>
                      </div>
                      <span className="fun-val num">{percent(row.ratio)} do topo</span>
                    </div>
                    {passam !== null && (
                      <div className="fun-conv">
                        <span className={passam < 0.6 ? "fc bad" : "fc"}>
                          ↓ passam <b>{percent(passam)}</b>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="card">
          <div className="card-title">
            <h2>Diagnóstico da IA</h2>
            <HelpPopover
              about="o diagnóstico da IA"
              label="Sobre o diagnóstico da IA"
              items={[
                {
                  term: "Como é lido",
                  description:
                    "São três perguntas fixas contadas sobre os registros do período: onde o funil mais vaza, quantas participações abertas estão sem interação real e qual origem converte acima da média. É contagem de regra, não modelo de IA.",
                },
              ]}
            />
            <span className="hint">
              {gaps.length === 0 ? "nada a apontar" : `${gaps.length} ${gaps.length === 1 ? "gap encontrado" : "gaps encontrados"}`}
            </span>
          </div>
          {gaps.length === 0 ? (
            <EmptyState
              title="Nada a apontar no período"
              description="As regras só falam com registro suficiente; sem isso ficam em silêncio, em vez de inventar conclusão."
            />
          ) : (
            <div className="gaps">
              {gaps.map((gap) => (
                <div className="gap-item" key={gap.key}>
                  <span className={`gap-sev ${gap.severity}`} aria-hidden="true" />
                  <div className="gap-body">
                    <div className="gt">{gap.title}</div>
                    <div className="gd">{gap.description}</div>
                    <button
                      type="button"
                      className="ga"
                      onClick={() => {
                        if (gap.key === "vazamento") navigate(`${appPath("funis")}/${funnelId}?aba=etapas`);
                        else if (gap.key === "follow-up") navigate(`${appPath("leads")}?visao=esfriando`);
                        else navigate(appPath("leads"));
                      }}
                    >
                      {gap.action} →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="ana-grid2">
        <section className="card">
          <div className="card-title">
            <h2>Performance por canal</h2>
            <HelpPopover
              about="o desempenho por canal"
              label="Sobre a performance por canal"
              items={[
                {
                  term: "Três marcos diferentes",
                  description:
                    "Leads contam pela data de criação; ganhos, pela data de fechamento da participação; receita, pela data do recebimento. Conversão é ganhos ÷ leads do período.",
                },
                { term: "A seta", description: "Compara a conversão com a janela anterior de mesma duração." },
              ]}
            />
            <span className="hint">{periodo}</span>
          </div>
          {channels.length === 0 ? (
            <EmptyState
              variant="no-results"
              title="Sem leads no período"
              description="Amplie o período para comparar os canais."
            />
          ) : (
            <table className="perf-table">
              <thead>
                <tr>
                  <th scope="col">Canal</th>
                  <th scope="col">Leads</th>
                  <th scope="col">Ganhos</th>
                  <th scope="col">Receita</th>
                  <th scope="col">Conversão</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((row, index) => (
                  <tr key={row.channel}>
                    <td>
                      <span className="pt-name">
                        <i aria-hidden="true" style={{ "--dot": CANAL_CORES[index % CANAL_CORES.length] } as CSSProperties} />
                        {row.channel}
                      </span>
                    </td>
                    <td className="num">{row.leads}</td>
                    <td className="num">{row.wins}</td>
                    <td className="num">{moneyCents(row.revenueCents)}</td>
                    <td>
                      <span className="num">{percent(row.conversion, 1)}</span>{" "}
                      {row.trend !== "flat" && (
                        <span className={`mini-delta ${row.trend}`}>
                          <span aria-hidden="true">{row.trend === "up" ? "▲" : "▼"}</span>
                          <span className="cx-sr-only">
                            {row.trend === "up" ? "acima" : "abaixo"} do período anterior
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card">
          <div className="card-title">
            <h2>Receita × meta por mês</h2>
            <HelpPopover
              about="a meta mensal"
              label="Sobre a receita contra a meta"
              items={[
                {
                  term: "Meta mensal",
                  description:
                    "A meta é declarada por período (o trimestre). O valor do mês é a meta dividida pelos meses que ela cobre; mês sem meta declarada não recebe linha.",
                },
              ]}
            />
            <span className="hint">linha = meta mensal</span>
          </div>
          <MonthlyChart months={months} />
        </section>
      </div>

      <section className="card ana-projecao">
        <div className="card-title">
          <h2>Projeção de receita</h2>
          <HelpPopover
            about="a projeção"
            label="Sobre a projeção de receita"
            items={[
              {
                term: "Denominador",
                description:
                  "Faturas já lançadas, não canceladas, com vencimento dentro da janela, contadas pelo saldo em aberto. Não há extrapolação de tendência nem previsão por modelo.",
              },
            ]}
          />
          <div className="ana-sel">
            <CompactSelect
              prefix="Período"
              options={PROJECAO}
              value={horizon}
              onChange={setHorizon}
              label="Janela da projeção"
              align="end"
            />
          </div>
        </div>
        <div className="z-toolbar">
          <strong className="num">{moneyCents(projection)}</strong>
          <span className="z-caption">
            {janela} · saldo de faturas já lançadas
          </span>
        </div>
        <ProjectionChart points={projectedDaily} />
        <p className="z-help">
          {overdue > 0 ? (
            <>
              Atrasados: <strong className="num">{moneyCents(overdue)}</strong> · separados da projeção. Valores
              previstos, ainda não recebidos.
            </>
          ) : (
            "Valores previstos pelos vencimentos da janela, ainda não recebidos."
          )}
        </p>
      </section>

      <div className="ana-grid3">
        <section className="card">
          <div className="card-title">
            <h2>LTV por cliente</h2>
            <HelpPopover
              about="o LTV"
              label="Sobre o LTV por cliente"
              items={[
                {
                  term: "Base",
                  description:
                    "A carteira ativa — leads com data de virada para cliente —, não o período selecionado. O valor é tudo o que já foi recebido de cada um.",
                },
                {
                  term: "Média e mediana",
                  description:
                    "Ficam lado a lado porque um contrato grande separa as duas; o topo 10% explica a distância.",
                },
              ]}
            />
            <span className="hint">base ativa</span>
          </div>
          <div className="ltv-big">
            <b className="num">{moneyCents(ltv.meanCents)}</b>
            <span>rendimento médio por cliente até hoje</span>
          </div>
          <div className="ltv-rows">
            <div className="dl-row">
              <span className="dl-name">Mediana</span>
              <span className="dl-val num">{moneyCents(ltv.medianCents)}</span>
            </div>
            <div className="dl-row">
              <span className="dl-name">Top 10% dos clientes</span>
              <span className="dl-val num">{moneyCents(topDecileCents(values))}</span>
            </div>
            <div className="dl-row">
              <span className="dl-name">Tempo médio como cliente</span>
              <span className="dl-val num">
                {tenure === null ? "—" : `${tenure.toFixed(1).replace(".", ",")} meses`}
              </span>
            </div>
            <div className="dl-row">
              <span className="dl-name">Clientes na carteira</span>
              <span className="dl-val num">{ltv.clients}</span>
            </div>
          </div>
        </section>

        <section className="card">
          <div className="card-title">
            <h2>Receita por canal</h2>
            <HelpPopover
              about="a receita por canal"
              label="Sobre a receita por canal"
              items={[
                {
                  term: "Origem",
                  description:
                    "É a origem gravada no recebimento, não a origem atual do lead: mudar o cadastro hoje não reescreve de onde veio o dinheiro de ontem.",
                },
              ]}
            />
            <span className="hint">{periodoCurto}</span>
          </div>
          {byChannel.length === 0 ? (
            <EmptyState
              variant="no-results"
              title="Sem recebimentos"
              description="Nenhum recebimento com data dentro do período."
            />
          ) : (
            <div className="donut-wrap">
              <DonutSlices
                className="donut-holder"
                slices={byChannel.map((row, index) => ({
                  label: row.channel,
                  value: row.cents,
                  color: CANAL_CORES[index % CANAL_CORES.length],
                }))}
                summary={byChannel.map((row) => `${row.channel}: ${moneyCents(row.cents)}`).join("; ")}
              >
                <b className="num">{`R$ ${short(donutTotal / 100)}`}</b>
                <span>total</span>
              </DonutSlices>
              <div className="donut-legend">
                {byChannel.map((row, index) => (
                  <div className="dl-row" key={row.channel}>
                    <span className="dl-name">
                      <i aria-hidden="true" style={{ background: CANAL_CORES[index % CANAL_CORES.length] }} />
                      {row.channel}
                    </span>
                    <span className="dl-val num">{percent(donutTotal > 0 ? row.cents / donutTotal : 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="card">
          <div className="card-title">
            <h2>Conversão por abordagem</h2>
            <HelpPopover
              about="a abordagem"
              label="Sobre a conversão por abordagem"
              items={[
                { term: "Abordagem", description: "É o gatilho registrado no lead." },
                {
                  term: "Denominador",
                  description:
                    "As participações encerradas no período — ganhas mais perdidas. Quem segue em aberto não entra em nenhum dos lados.",
                },
              ]}
            />
            <span className="hint">{periodoCurto}</span>
          </div>
          {approaches.length === 0 ? (
            <EmptyState
              variant="no-results"
              title="Sem gatilhos encerrados"
              description="Marque gatilhos nos leads para comparar abordagens."
            />
          ) : (
            <div className="chan">
              {approaches.map((row) => (
                <div className="chan-row" key={row.name}>
                  <span className="cname" title={row.name}>
                    {row.name}
                  </span>
                  <span className="chan-bar">
                    <i style={{ width: `${Math.round(row.ratio * 100)}%` }} aria-hidden="true" />
                  </span>
                  <span className="chan-val num">{percent(row.ratio)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <div className="card-title">
            <h2>Aproveitamento</h2>
            <HelpPopover
              about="o aproveitamento"
              label="Sobre o aproveitamento"
              items={[
                {
                  term: "Fórmula",
                  description:
                    "Participações ganhas ÷ encerradas (ganhas mais perdidas), acumulado dia a dia dentro do período. É acumulado porque a taxa de um único dia oscila entre 0% e 100% e não descreve nada.",
                },
              ]}
            />
            <span className="hint">{periodoCurto}</span>
          </div>
          <UtilizationChart series={utilization} range={range} />
        </section>
      </div>

      <div className="ana-grid4">
        <section className="card card-area">
          <div className="card-title">
            <h2>Ritmo dos canais</h2>
            <HelpPopover
              about="o ritmo"
              label="Sobre o ritmo dos canais"
              items={[
                {
                  term: "Ritmo",
                  description:
                    "Recebido por dia, separado pela origem gravada no recebimento, dentro do período. Mostra as três origens de maior volume.",
                },
              ]}
            />
            <div className="area-legend">
              {pace.length === 0 ? (
                <span className="hint">sem recebimento no período</span>
              ) : (
                pace.map((row, index) => (
                  <span key={row.channel}>
                    <i aria-hidden="true" style={{ background: CANAL_CORES[index % CANAL_CORES.length] }} />
                    {row.channel}
                  </span>
                ))
              )}
            </div>
          </div>
          <PaceChart
            channels={pace.map((row, index) => ({
              channel: row.channel,
              values: row.values,
              totalCents: row.totalCents,
              color: CANAL_CORES[index % CANAL_CORES.length],
            }))}
            range={range}
          />
        </section>
      </div>
      {reportOpen && <ReportDialog onClose={() => setReportOpen(false)} period={periodParam} funnelId={funnelId} />}
    </div>
  );
}

/**
 * Moldura comum dos gráficos: régua de valores à esquerda, área de desenho e
 * eixo de tempo embaixo.
 *
 * O SVG desenha só as formas, com escala livre; todo texto é HTML, para não
 * esticar junto com a largura do cartão. As linhas de grade e os dados usam as
 * mesmas frações da altura, então rótulo e traço coincidem.
 */
function Plot({
  ticks,
  axis,
  height,
  overlay,
  summary,
  children,
  className,
}: {
  /** Rótulos do topo para a base. */
  ticks: string[];
  axis: ReactNode;
  height: number;
  overlay?: ReactNode;
  /** O mesmo que o desenho mostra, em texto: o gráfico nunca é o único acesso
   *  ao número (AGENTS §17). */
  summary: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className ? `ana-plot ${className}` : "ana-plot"}>
      <div className="ana-ticks" style={{ height }} aria-hidden="true">
        {ticks.map((tick, index) => (
          <span key={`${tick}-${index}`} style={{ top: `${(index / Math.max(ticks.length - 1, 1)) * 100}%` }}>
            {tick}
          </span>
        ))}
      </div>
      <div className="ana-canvas" style={{ height }}>
        <svg
          viewBox={`0 0 ${PLOT_W} ${height}`}
          preserveAspectRatio="none"
          role="presentation"
          aria-hidden="true"
        >
          {GRID.map((fraction) => (
            <line
              key={fraction}
              className="ana-grid"
              x1={0}
              x2={PLOT_W}
              y1={height * fraction}
              y2={height * fraction}
            />
          ))}
          {children}
        </svg>
        {overlay}
      </div>
      <div className="ana-axis">{axis}</div>
      <span className="cx-sr-only">{summary}</span>
    </div>
  );
}

const PLOT_W = 640;
const GRID = [0, 0.25, 0.5, 0.75, 1];

/** Régua de valores em reais, do topo para a base. */
function moneyTicks(top: number): string[] {
  return [1, 0.75, 0.5, 0.25, 0].map((fraction) => short((top * fraction) / 100));
}

/** Soma a série em até `max` trechos consecutivos; o total não muda. */
function sumBuckets(values: readonly number[], max = 12): number[] {
  const size = Math.max(1, Math.ceil(values.length / max));
  const out: number[] = [];
  for (let index = 0; index < values.length; index += size) {
    out.push(values.slice(index, index + size).reduce((sum, value) => sum + value, 0));
  }
  return out;
}

function pointsOf(values: readonly number[], top: number, height: number): Array<readonly [number, number]> {
  return values.map(
    (value, index) =>
      [
        (index * PLOT_W) / Math.max(values.length - 1, 1),
        height - (value / top) * height,
      ] as const,
  );
}

/** Barras do recebido por mês, com a linha da meta mensal por cima. */
function MonthlyChart({ months }: { months: readonly MonthRevenue[] }) {
  const height = 186;
  const top = Math.max(...months.map((month) => Math.max(month.cents, month.goalCents ?? 0)), 1) * 1.15;
  const goal = months.find((month) => month.goalCents !== null)?.goalCents ?? null;

  return (
    <Plot
      className="ana-plot-bars"
      height={height}
      ticks={moneyTicks(top)}
      summary={months
        .map(
          (month) =>
            `${month.label}: ${moneyCents(month.cents)}${
              month.goalCents === null ? ", sem meta declarada" : `, meta ${moneyCents(month.goalCents)}`
            }`,
        )
        .join("; ")}
      axis={months.map((month) => (
        <span key={month.month}>{month.label}</span>
      ))}
      overlay={
        <>
          {/* As barras são HTML, não retângulos do SVG esticado: assim a borda
              arredondada não deforma e a largura tem o teto de 72px do
              protótipo, com o gradiente e o brilho dele. */}
          <div className="ana-barlabels" aria-hidden="true">
            {months.map((month, index) => (
              <span key={month.month} style={{ "--v": month.cents / top } as CSSProperties}>
                {month.cents > 0 && <i className="ana-bar" style={{ animationDelay: `${index * 90}ms` }} />}
                <b>{month.cents === 0 ? "" : short(month.cents / 100)}</b>
              </span>
            ))}
          </div>
          {goal !== null && (
            <span className="ana-goal-tag" style={{ "--v": goal / top } as CSSProperties} aria-hidden="true">
              meta {short(goal / 100)}/mês
            </span>
          )}
        </>
      }
    >
      {goal !== null && (
        <line className="ana-goal" x1={0} x2={PLOT_W} y1={height - (goal / top) * height} y2={height - (goal / top) * height} />
      )}
    </Plot>
  );
}

/**
 * Previsto na janela, agrupado em até 12 trechos. Vencimento é esparso: dia a
 * dia, a curva vira uma fileira de agulhas; por trecho, fica a leitura do
 * protótipo. Cada trecho soma os seus dias, então o total não muda.
 */
function ProjectionChart({ points: daily }: { points: readonly { date: string; cents: number }[] }) {
  const height = 186;
  const size = Math.max(1, Math.ceil(daily.length / 12));
  const buckets: Array<{ date: string; cents: number }> = [];
  for (let index = 0; index < daily.length; index += size) {
    const slice = daily.slice(index, index + size);
    buckets.push({ date: slice[0].date, cents: slice.reduce((sum, point) => sum + point.cents, 0) });
  }
  const series = buckets.map((bucket) => bucket.cents);
  const top = Math.max(...series, 1) * 1.15;
  const points = pointsOf(series, top, height);
  const line = points.length > 1 ? smoothPath(points) : "";

  return (
    <Plot
      height={height}
      ticks={moneyTicks(top)}
      summary={`Previsto por trecho da janela: ${buckets
        .map((bucket) => `${shortDay(bucket.date)} ${moneyCents(bucket.cents)}`)
        .join(", ")}.`}
      axis={buckets.map((bucket) => (
        <span key={bucket.date}>{shortDay(bucket.date)}</span>
      ))}
    >
      {line && <path className="ana-area" d={`${line} L${PLOT_W},${height} L0,${height} Z`} />}
      {line && <path className="ana-line" d={line} />}
    </Plot>
  );
}

function UtilizationChart({ series, range }: { series: number[]; range: DayRange }) {
  const height = 150;
  const points = series.map(
    (value, index) =>
      [(index * PLOT_W) / Math.max(series.length - 1, 1), height - value * height] as const,
  );
  const line = points.length > 1 ? smoothPath(points) : "";
  const last = series.at(-1) ?? 0;

  return (
    <Plot
      className="ana-plot-sm"
      height={height}
      ticks={[1, 0.75, 0.5, 0.25, 0].map((fraction) => percent(fraction))}
      summary={`Aproveitamento acumulado no fim do período: ${percent(
        last,
        1,
      )} das participações encerradas foram ganhas.`}
      axis={
        <>
          <span>{shortDay(range.from)}</span>
          <span>{shortDay(range.to)}</span>
        </>
      }
      overlay={
        <span className="ana-marker" style={{ "--v": last } as CSSProperties} aria-hidden="true">
          {shortDay(range.to)} · {percent(last, 1)}
        </span>
      }
    >
      {line && <path className="ana-area alt" d={`${line} L${PLOT_W},${height} L0,${height} Z`} />}
      {line && <path className="ana-line alt" d={line} />}
    </Plot>
  );
}

function PaceChart({
  channels,
  range,
}: {
  channels: Array<{ channel: string; values: number[]; totalCents: number; color: string }>;
  range: DayRange;
}) {
  const height = 186;
  /* Por trecho, não por dia: recebimento é esparso e a série diária de um
     trimestre desenhava agulhas no lugar das ondas do protótipo. */
  const grouped = channels.map((channel) => ({ ...channel, values: sumBuckets(channel.values) }));
  const top = Math.max(...grouped.flatMap((channel) => channel.values), 1) * 1.15;

  return (
    <Plot
      height={height}
      ticks={moneyTicks(top)}
      summary={
        channels.length === 0
          ? "Sem recebimento no período."
          : channels.map((channel) => `${channel.channel}: ${moneyCents(channel.totalCents)}`).join("; ")
      }
      axis={
        <>
          <span>{shortDay(range.from)}</span>
          <span>{shortDay(range.to)}</span>
        </>
      }
    >
      {grouped.map((channel) => {
        const points = pointsOf(channel.values, top, height);
        const line = points.length > 1 ? smoothPath(points) : "";
        if (!line) return null;
        return (
          <g key={channel.channel}>
            <path d={`${line} L${PLOT_W},${height} L0,${height} Z`} fill={channel.color} opacity={0.24} />
            <path d={line} fill="none" stroke={channel.color} strokeWidth={2} />
          </g>
        );
      })}
    </Plot>
  );
}
